import React, { useEffect, useState } from 'react';
import { Box, TextField, FormControl, MenuItem } from '@mui/material';
import { useAppDispatch } from '../../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { useDebounce } from '../../../../../hooks/useDebounce';
import { IGeoData } from '../../../../../models/service/service';
import { searchPlaces } from '../../../../../store/actions/actions';
import { organizerSlice } from '../../../../../store/slice/slice';
import { errorNames, errorRegExp } from '../../../../../utils/constants';
import { ErrorText } from '../../../../errorText/ErrorText';
import { borderInput } from '../../../../formAddress/formAddress.styles';
import { PhoneInput } from '../../../../phoneInput';
import { boxStyles, useStyles, inputRowStyles } from '../../modalEditProfile.styles';
import { IPropsForm } from '../../types';

export const ContactControl = (props: IPropsForm): JSX.Element => {
  const { control, register, required, errors, setValue, setAddress, address } = props;
  const [clickAddress, setClickAddress] = useState(false);
  const [addressErrors, setAddressErrors] = useState(false);
  const debouncedSearchAddress = useDebounce(address || '', 500);
  const geoData = useAppSelector((state) => state.organizerReducer.geoData);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  useEffect(() => {
    if (debouncedSearchAddress && clickAddress) {
      searchPlaces(debouncedSearchAddress)(dispatch);
    }
  }, [debouncedSearchAddress]);

  const handleChangeAddress = (value: string) => {
    setClickAddress(true);
    setAddress && setAddress(value);
    setValue('address', value);
    if (value.length > 200) {
      setAddressErrors(true);
    }
    if (value.length < 200) {
      setAddressErrors(false);
    }
  };

  const handleClickAddress = (value: string) => {
    handleChangeAddress(value);
    dispatch(organizerSlice.actions.setAddresses([]));
    setClickAddress(false);
  };

  return (
    <Box sx={boxStyles}>
      <TextField
        fullWidth
        autoComplete="off"
        label="Organisation name*"
        id="org name"
        {...register('name', { required, maxLength: 100 })}
        className={classes.modalInp}
        error={!!errors?.name}
        sx={borderInput}
      />
      <div className={classes.errors}>
        {errors?.name?.type === 'required' && <ErrorText text={errorNames.nameRequired} />}
        {errors?.name?.type === 'maxLength' && <ErrorText text={errorNames.nameLength} />}
      </div>
      <TextField
        fullWidth
        label="Contact name"
        id="cont name"
        autoComplete="off"
        {...register('contactName', { maxLength: 100 })}
        className={classes.modalInp}
        sx={borderInput}
      />
      <div className={classes.errors}>
        {errors?.contactName?.type === 'maxLength' && <ErrorText text={errorNames.contactNameLength} />}
      </div>
      <div className={classes.inpBlock}>
        <Box sx={inputRowStyles}>
          <FormControl fullWidth>
            <TextField
              disabled
              id="Email"
              label="Email*"
              autoComplete="off"
              {...register('email', {
                required,
                pattern: errorRegExp.email,
              })}
              className={classes.modalContactInp}
              error={!!errors?.email}
              sx={borderInput}
            />
            <div className={classes.emailErrors}>
              {errors?.email?.type === 'required' && <ErrorText text={errorNames.emailRequired} />}
              {errors?.email?.type === 'pattern' && <ErrorText text={errorNames.invalidEmail} />}
            </div>
          </FormControl>
          <FormControl fullWidth>
            <PhoneInput
              control={control}
              require={false}
              fullWidth
              id="Phone number"
              label="Phone number"
              {...register('phoneNumber')}
              error={!!errors?.phoneNumber}
              className={classes.modalContactInp}
              sx={borderInput}
            />
            <div className={classes.contactErrors}>
              {errors?.phoneNumber?.type === 'pattern' && <ErrorText text={errorNames.invalidPhone} />}
            </div>
          </FormControl>
        </Box>
        <Box sx={inputRowStyles}>
          <FormControl className={classes.formAddress} fullWidth>
            <TextField
              id="outlined-basic"
              label="Address"
              value={address}
              onChange={(e): void => handleChangeAddress(e.target.value)}
              className={classes.modalContactInp}
              error={addressErrors}
              sx={borderInput}
            />
            {geoData && geoData.map((item: IGeoData) => (
              <MenuItem
                key={item.place_id}
                value={item.formatted_address}
                onClick={(): void => handleClickAddress(item.formatted_address)}
              >
                {item.formatted_address}
              </MenuItem>
            ))}
            <div className={classes.contactErrors}>
              {addressErrors && <ErrorText text={errorNames.addressLength} />}
            </div>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              id="link"
              fullWidth
              autoComplete="off"
              label="Website link"
              {...register('websiteUrl', { maxLength: 100 })}
              className={classes.modalContactInp}
              sx={borderInput}
            />
            <div className={classes.contactErrors}>
              {errors?.websiteUrl?.type === 'maxLength' && <ErrorText text={errorNames.websiteLength} />}
            </div>
          </FormControl>
        </Box>
      </div>
    </Box>
  );
};
